import React from "react";
import { Instagram, Facebook, Music2, Youtube, MapPin } from "lucide-react";

/**
 * ProviderChips
 *
 * Mostra a colpo d'occhio le piattaforme Metricool su cui un item della
 * rubrica (foto o video) verrà pubblicato. Due modalità:
 *
 *   • readonly (default): chip non cliccabili, attive = colorate, spente = grigio.
 *     Usate nella vista lista delle rubriche per dare un'occhiata veloce.
 *
 *   • editable (`onChange` passato): chip cliccabili → toggle on/off.
 *     Usate nel modal di edit (matitina) per modificare le destinazioni
 *     per quel singolo item.
 *
 * Provider supportati: instagram, facebook, tiktok, youtube, gmb.
 * `allowed` restringe quali chip mostrare (es. la rubrica foto mostra solo
 * gmb/facebook/instagram perché TikTok/YouTube non supportano foto).
 */

const PROVIDER_DEFS = {
  instagram: {
    label: "Instagram",
    short: "IG",
    Icon: Instagram,
    color: "#E1306C",
  },
  facebook: {
    label: "Facebook",
    short: "FB",
    Icon: Facebook,
    color: "#1877F2",
  },
  tiktok: {
    label: "TikTok",
    short: "TT",
    Icon: Music2,
    color: "#111111",
  },
  youtube: {
    label: "YouTube",
    short: "YT",
    Icon: Youtube,
    color: "#FF0000",
  },
  gmb: {
    label: "Google",
    short: "GMB",
    Icon: MapPin,
    color: "#4285F4",
  },
};

export default function ProviderChips({
  active = [],
  allowed = ["instagram", "facebook", "tiktok", "youtube", "gmb"],
  onChange = null,
  size = "sm",
  testidPrefix = "provider-chip",
}) {
  const activeSet = new Set((active || []).map((p) => (p || "").toLowerCase()));
  const editable = typeof onChange === "function";

  const toggle = (key) => {
    if (!editable) return;
    const next = new Set(activeSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // preserve the same order as `allowed` so the array stays predictable
    onChange(allowed.filter((p) => next.has(p)));
  };

  const dim = size === "xs" ? "h-5 text-[10px] px-1.5" : "h-7 text-xs px-2.5";
  const iconSize = size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5";

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-row`}>
      {allowed.map((key) => {
        const def = PROVIDER_DEFS[key];
        if (!def) return null;
        const isActive = activeSet.has(key);
        const Icon = def.Icon;
        const baseCls = `inline-flex items-center gap-1 rounded-full border font-semibold transition-colors select-none ${dim}`;
        const stateCls = isActive
          ? "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]"
          : "bg-white text-[var(--mp-muted)] border-[var(--mp-border)]";
        const interCls = editable
          ? "cursor-pointer hover:border-[var(--mp-ink)]"
          : "cursor-default";
        const title = `${def.label}${isActive ? "" : " (spento)"}`;
        if (editable) {
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              title={title}
              data-testid={`${testidPrefix}-${key}`}
              data-active={isActive ? "true" : "false"}
              className={`${baseCls} ${stateCls} ${interCls}`}
            >
              <Icon className={iconSize} style={isActive ? { color: def.color } : undefined} />
              <span>{size === "xs" ? def.short : def.label}</span>
            </button>
          );
        }
        return (
          <span
            key={key}
            title={title}
            data-testid={`${testidPrefix}-${key}`}
            data-active={isActive ? "true" : "false"}
            className={`${baseCls} ${stateCls} ${interCls}`}
          >
            <Icon className={iconSize} style={isActive ? { color: def.color } : undefined} />
            <span>{size === "xs" ? def.short : def.label}</span>
          </span>
        );
      })}
    </div>
  );
}
